"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";

const LINKS = [
  { href: "/sites", label: "אתרים" },
  { href: "/inspections", label: "בדיקות חודשיות" },
  { href: "/annual-checks", label: "בדקים שנתיים" },
  { href: "/five-year-checks", label: "בדק חמש שנתי" },
  { href: "/faults", label: "תקלות" },
  { href: "/fueling", label: "תדלוקים" },
  { href: "/batteries", label: "החלפות מצבר" },
  { href: "/contacts", label: "אנשי קשר" },
  { href: "/fuel-calculator", label: "מחשבון דלק" },
  { href: "/dev-requests", label: "בקשות לפיתוח" },
];

const ROLE_LABELS: Record<string, string> = {
  ADMIN: "מנהל/ת",
  ENTRY: "הזנת נתונים",
  VIEWER: "צפייה בלבד",
};

function TrainMark() {
  return (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary text-white shadow-sm">
      <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5">
        <path
          d="M5 16.5V8a3 3 0 0 1 3-3h8a3 3 0 0 1 3 3v8.5"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <rect x="5" y="8" width="14" height="6" rx="1" fill="currentColor" fillOpacity="0.35" />
        <path d="M3 19h18" stroke="var(--brand-sky)" strokeWidth="2" strokeLinecap="round" />
        <circle cx="8.5" cy="16.5" r="1.4" fill="currentColor" />
        <circle cx="15.5" cy="16.5" r="1.4" fill="currentColor" />
      </svg>
    </div>
  );
}

function NavLink({
  href,
  label,
  onClick,
  variant = "block",
}: {
  href: string;
  label: string;
  onClick?: () => void;
  variant?: "tab" | "block";
}) {
  const pathname = usePathname();
  const active = href === "/" ? pathname === "/" : pathname.startsWith(href);

  if (variant === "tab") {
    return (
      <Link
        href={href}
        onClick={onClick}
        className={`flex h-full items-center whitespace-nowrap border-b-2 px-2 text-[13px] font-medium transition-colors ${
          active
            ? "border-yellow text-primary"
            : "border-transparent text-text-muted hover:text-text"
        }`}
      >
        {label}
      </Link>
    );
  }

  return (
    <Link
      href={href}
      onClick={onClick}
      className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
        active ? "bg-primary/10 text-primary" : "text-text-muted hover:bg-surface-muted hover:text-text"
      }`}
    >
      {label}
    </Link>
  );
}

export function NavBarClient({
  session,
  logout,
}: {
  session: { email: string; role: string } | null;
  logout: () => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-surface/95 backdrop-blur supports-[backdrop-filter]:bg-surface/80">
      <div className="mx-auto flex max-w-[120rem] items-center justify-between gap-3 px-4 py-2.5 sm:px-6">
        <Link href="/" className="flex shrink-0 items-center gap-2.5" onClick={() => setOpen(false)}>
          <TrainMark />
          <span className="leading-tight">
            <span className="block text-sm font-bold text-text">תפעול גנרטורים</span>
            <span className="hidden text-[11px] text-text-muted xl:block">יחידת חשמל · רכבת ישראל</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-0.5 self-stretch min-[2150px]:flex">
          {LINKS.map((link) => (
            <NavLink key={link.href} {...link} variant="tab" />
          ))}
        </nav>

        <div className="hidden shrink-0 items-center gap-1.5 min-[2150px]:flex">
          {session && session.role !== "VIEWER" && (
            <Link href="/" className="btn btn-primary whitespace-nowrap">
              + הזנת נתונים
            </Link>
          )}
          {session?.role === "ADMIN" && (
            <>
              <NavLink href="/sites/new" label="+ אתר חדש" />
              <NavLink href="/finance" label="בקרת תשלומים" />
              <NavLink href="/price-list" label="מחירון" />
              <a
                href="/export"
                className="whitespace-nowrap rounded-md px-2 py-2 text-[13px] font-medium text-text-muted transition-colors hover:bg-surface-muted hover:text-text"
              >
                ייצוא לאקסל
              </a>
              <NavLink href="/export/annual" label="ייצוא שנתי" />
              <NavLink href="/users" label="ניהול משתמשים" />
            </>
          )}
          {session && (
            <Link
              href="/help"
              title="עזרה — מדריך משתמש"
              aria-label="עזרה — מדריך משתמש"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border text-[13px] font-bold text-text-muted transition-colors hover:border-primary/40 hover:text-primary"
            >
              ?
            </Link>
          )}
          {session && (
            <div className="mr-1 flex shrink-0 items-center gap-2 border-r border-border pr-2 text-[13px] text-text-muted">
              <span className="hidden max-w-[12ch] truncate xl:inline">
                {session.email} <span className="text-text-muted/70">({ROLE_LABELS[session.role] ?? session.role})</span>
              </span>
              <form action={logout}>
                <button type="submit" className="link-back whitespace-nowrap">
                  התנתקות
                </button>
              </form>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label="תפריט"
          aria-expanded={open}
          className="flex h-9 w-9 items-center justify-center rounded-md text-text hover:bg-surface-muted min-[2150px]:hidden"
        >
          <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5">
            {open ? (
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            ) : (
              <path
                d="M4 7h16M4 12h16M4 17h16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            )}
          </svg>
        </button>
      </div>

      {open && (
        <div className="border-t border-border bg-surface px-4 pb-4 pt-2 min-[2150px]:hidden">
          <nav className="flex flex-col gap-1">
            {LINKS.map((link) => (
              <NavLink key={link.href} {...link} onClick={() => setOpen(false)} />
            ))}
          </nav>

          {session && session.role !== "VIEWER" && (
            <Link
              href="/"
              onClick={() => setOpen(false)}
              className="btn btn-primary mt-3 w-full"
            >
              + הזנת נתונים
            </Link>
          )}

          {session?.role === "ADMIN" && (
            <div className="mt-2 flex flex-col gap-1 border-t border-border pt-2">
              <NavLink href="/sites/new" label="+ אתר חדש" onClick={() => setOpen(false)} />
              <NavLink href="/finance" label="בקרת תשלומים" onClick={() => setOpen(false)} />
              <NavLink href="/price-list" label="מחירון" onClick={() => setOpen(false)} />
              <a
                href="/export"
                className="rounded-md px-3 py-2 text-sm font-medium text-text-muted transition-colors hover:bg-surface-muted hover:text-text"
              >
                ייצוא לאקסל
              </a>
              <NavLink href="/export/annual" label="ייצוא שנתי" onClick={() => setOpen(false)} />
              <NavLink href="/users" label="ניהול משתמשים" onClick={() => setOpen(false)} />
            </div>
          )}

          {session && (
            <div className="mt-2 border-t border-border pt-2">
              <NavLink href="/help" label="עזרה — מדריך משתמש" onClick={() => setOpen(false)} />
            </div>
          )}

          {session && (
            <div className="mt-3 flex items-center justify-between border-t border-border pt-3 text-sm text-text-muted">
              <span className="truncate">
                {session.email} ({ROLE_LABELS[session.role] ?? session.role})
              </span>
              <form action={logout}>
                <button type="submit" className="link-back">
                  התנתקות
                </button>
              </form>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
